import React, { useLayoutEffect, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ButtonBase from '@mui/material/ButtonBase';
import useMediaQuery from '@mui/material/useMediaQuery';
import BackspaceRoundedIcon from '@mui/icons-material/BackspaceRounded';
import SendRoundedIcon from '@mui/icons-material/SendRounded';
import { letterKeypadLayout, normalizeTurkishLetters } from './turkishLetterKeyboard';

// Shared 4–29 letter keypad. Same purple visual language as DigitKeypad,
// without altering numeric-entry screens. Balanced rows stay centered.
export default function AdaptiveLetterKeypad({
  alphabet, disabled = false, onLetter, onDelete, onSubmit,
  submitDisabled = false, submitLabel = 'Yazdım',
}) {
  const narrow = useMediaQuery('(max-width:359px)');
  const phone = useMediaQuery('(max-width:699px)');
  const tablet = useMediaQuery('(max-width:1023px)');
  const smartboard = useMediaQuery('(min-width:1600px)');
  const legacySm = useMediaQuery('(min-width:600px)');
  const legacyMd = useMediaQuery('(min-width:900px)');
  const letters = normalizeTurkishLetters(alphabet);
  const compact = letters.length <= 4;
  const columns = compact ? Math.max(1, letters.length) : narrow ? 6 : phone ? 7 : tablet ? 9 : 10;
  const gap = compact ? legacyMd ? 9.2 : legacySm ? 8 : 6.8 : narrow ? 2 : phone ? 4 : tablet ? 6 : 8;
  const compactWidth = legacyMd ? 560 : legacySm ? 430 : 304;
  const keyWidth = compact ? (compactWidth - (columns - 1) * gap) / columns : phone ? 44 : tablet ? 60 : smartboard ? 86 : 70;
  const keyHeight = compact ? legacyMd ? 72 : legacySm ? 62 : 52 : phone ? 50 : tablet ? 64 : smartboard ? 90 : 72;
  const host = useRef(null);
  const [availableWidth, setAvailableWidth] = useState(0);
  useLayoutEffect(() => {
    if (host.current.clientWidth > 0) setAvailableWidth(host.current.clientWidth);
    if (typeof ResizeObserver !== 'function') return undefined;
    const observer = new ResizeObserver(entries => {
      if (entries[0]?.contentRect.width > 0) setAvailableWidth(entries[0].contentRect.width);
    });
    observer.observe(host.current);
    return () => observer.disconnect();
  }, []);
  const layout = letterKeypadLayout(letters, { columns, keyWidth, gap, availableWidth });
  const focus = { outline: '4px solid #4c1d95', outlineOffset: -5 };
  return <Box ref={host} sx={{ maxWidth: layout.maxWidth, minWidth: 0, mx: 'auto', width: '100%' }}>
    <Box role="group" aria-label="Harf klavyesi" sx={{ display: 'grid', gap: gap + 'px' }}>
      {layout.rows.map((row, index) => <Box key={index} data-testid="letter-keyboard-row" sx={{
        display: 'flex', justifyContent: 'center', gap: gap + 'px',
      }}>
        {row.map(letter => <ButtonBase
          key={letter} type="button" aria-label={letter + ' harfi'}
          disabled={disabled} onClick={() => onLetter(letter)}
          sx={{ width: layout.keyWidth, height: keyHeight, flex: '0 0 auto',
            border: '3px solid #d8b4fe', borderRadius: compact ? { xs: '15px', sm: '17px', md: '20px' } : { xs: '14px', md: '19px' },
            bgcolor: '#fff', color: '#251777', fontWeight: 1000,
            fontSize: compact ? legacyMd ? 42 : legacySm ? 34 : 28 : phone ? 25 : tablet ? 32 : smartboard ? 42 : 37,
            boxShadow: '0 4px 0 rgba(109,40,217,0.13), 0 8px 16px rgba(76,29,149,0.08)',
            '&:hover': { bgcolor: '#fdfbff' },
            '&:active': { transform: 'translateY(2px)' },
            '&:focus-visible': focus,
            '&.Mui-disabled': { color: '#8c819b', bgcolor: '#ece8f1',
              borderColor: '#d8d0e2', boxShadow: 'none' },
          }}>{letter}</ButtonBase>)}
      </Box>)}
    </Box>
    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1.35fr',
      gap: { xs: 1, md: 1.3 }, mt: { xs: 1.1, md: 1.4 }, maxWidth: 560, mx: 'auto' }}>
      <Button variant="outlined" disableRipple startIcon={<BackspaceRoundedIcon />}
        disabled={disabled} onClick={onDelete}
        sx={{ minHeight: { xs: 50, md: 62 }, borderRadius: 2.5,
          borderWidth: 3, borderColor: '#d8b4fe', color: '#4c1d95',
          fontWeight: 1000, fontSize: { xs: 16, md: 20 }, textTransform: 'none', '&:focus-visible': focus }}>Sil</Button>
      <Button variant="contained" disableRipple endIcon={<SendRoundedIcon />}
        disabled={disabled || submitDisabled} onClick={onSubmit}
        sx={{ minHeight: { xs: 50, md: 62 }, borderRadius: 2.5, bgcolor: '#6d45df',
          fontWeight: 1000, fontSize: { xs: 16, md: 20 }, textTransform: 'none',
          boxShadow: '0 5px 0 rgba(76,29,149,0.25)', '&:hover': { bgcolor: '#5d31d2' },
          '&:focus-visible': focus }}>{submitLabel}</Button>
    </Box>
  </Box>;
}
